import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/useEntity';
import { fmt } from '../../lib/formatters';
import { confirmar, toast } from '../../lib/toast';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { FormRow } from '../ui/FormRow';
import { Spinner } from '../ui/Spinner';
import { DataTable, type Column } from '../DataTable';

/**
 * Secciones «Histórico de precio de venta» y «Entregas» del producto,
 * compartidas por la ficha nueva (ProductoFicha) y la clásica
 * (ProductoDetalle). Extraídas en el rediseño UX (fase 1/3, 16/09/2026);
 * las rutas a pedidos se derivan del cliente (antes: /pedidos-cliente/:id,
 * que no existía, y /game/ fijo).
 */

// ─────────────────────────────────────────────────────────────────────
// Entregas del producto: histórico + alta de nueva entrega contra una
// de las líneas pendientes (cross-pedido). Listado y formulario de alta.
// ─────────────────────────────────────────────────────────────────────

type EntregaRow = {
  id: string;
  linea_id: string;
  fecha_entrega: string;
  unidades: number;
  notas: string | null;
  created_at: string;
  created_by_email: string | null;
  linea_unidades: number;
  linea_entregadas: number;
  linea_pendientes: number;
  pedido_cliente_id: string;
  numero_cliente: string | null;
  numero_approx: string | null;
  fecha_pedido: string;
  pedido_estado: string;
  pedido_estado_nombre: string | null;
  pedido_estado_color: string | null;
};

type LineaPendiente = {
  linea_id: string;
  unidades: number;
  unidades_entregadas: number;
  pendientes: number;
  pedido_cliente_id: string;
  numero_cliente: string | null;
  numero_approx: string | null;
  fecha_pedido: string;
  pedido_estado: string;
};

// ─────────────────────────────────────────────────────────────────────
// Histórico de precio de venta: a qué precio se le ha vendido este
// producto al cliente en cada pedido. Se lee de las líneas de pedido, que
// son la fuente de verdad; no hay tabla de histórico que pueda divergir.
// ─────────────────────────────────────────────────────────────────────

type PrecioVentaRow = {
  linea_id: string;
  pedido_cliente_id: string;
  fecha_pedido: string;
  numero_cliente: string | null;
  numero_approx: string | null;
  unidades: number;
  precio_unitario: string | number;
  importe: string | number;
  fecha_entrega_real: string | null;
  pedido_estado: string;
  pedido_estado_nombre: string | null;
  pedido_estado_color: string | null;
  variacion_pct: string | number | null;
};

export function HistoricoPrecioVentaSection({ productoId, clienteCodigo }: { productoId: string; clienteCodigo: string }) {
  const base = `/${clienteCodigo.toLowerCase()}`;
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['producto', productoId, 'historico-precio-venta'],
    queryFn: () => api.get<{ historico: PrecioVentaRow[] }>(`/productos/${productoId}/historico-precio-venta`),
  });
  const rows = q.data?.historico ?? [];

  const cols: Column<PrecioVentaRow>[] = [
    {
      key: 'fecha_pedido', label: 'Fecha pedido',
      render: (r) => fmt.date(r.fecha_pedido), sortValue: (r) => r.fecha_pedido,
    },
    {
      key: 'numero_cliente', label: 'Pedido',
      render: (r) => <span className="font-mono">{r.numero_cliente ?? r.numero_approx ?? '—'}</span>,
      sortValue: (r) => r.numero_cliente ?? '',
    },
    {
      key: 'precio_unitario', label: '€/ud', align: 'right',
      render: (r) => <strong>{fmt.eur(r.precio_unitario)}</strong>,
      sortValue: (r) => Number(r.precio_unitario), total: 'avg', totalFormat: (n) => fmt.eur(n),
    },
    {
      key: 'variacion_pct', label: 'Var. vs anterior', align: 'right',
      render: (r) => {
        if (r.variacion_pct == null) return <span className="text-slate-400">—</span>;
        const v = Number(r.variacion_pct);
        if (v === 0) return <span className="text-slate-500">0,00 %</span>;
        return (
          <span className={v > 0 ? 'text-ok-700' : 'text-bad-600'}>
            {v > 0 ? '+' : ''}{fmt.number(v)} %
          </span>
        );
      },
      sortValue: (r) => (r.variacion_pct == null ? 0 : Number(r.variacion_pct)),
    },
    {
      key: 'unidades', label: 'Uds.', align: 'right',
      render: (r) => fmt.int(r.unidades), sortValue: (r) => Number(r.unidades), total: 'sum', totalFormat: (n) => fmt.int(n),
    },
    {
      key: 'importe', label: 'Importe', align: 'right',
      render: (r) => fmt.eur(r.importe), sortValue: (r) => Number(r.importe), total: 'sum', totalFormat: (n) => fmt.eur(n),
    },
    {
      key: 'fecha_entrega_real', label: 'Entregado',
      render: (r) => (r.fecha_entrega_real ? fmt.date(r.fecha_entrega_real) : '—'),
      sortValue: (r) => r.fecha_entrega_real ?? '',
    },
    {
      key: 'pedido_estado', label: 'Estado',
      render: (r) => <Badge color={r.pedido_estado_color} dot>{r.pedido_estado_nombre ?? r.pedido_estado}</Badge>,
      sortValue: (r) => r.pedido_estado_nombre ?? r.pedido_estado,
    },
  ];

  const precios = rows.map((r) => Number(r.precio_unitario)).filter((n) => Number.isFinite(n));
  const resumen = precios.length > 0
    ? `${rows.length} pedido${rows.length === 1 ? '' : 's'} · precio entre ${fmt.eur(Math.min(...precios))} y ${fmt.eur(Math.max(...precios))} · último ${fmt.eur(precios[0])}.`
    : 'Todavía no se ha vendido este producto en ningún pedido de cliente con precio.';

  return (
    <Card
      title="Histórico de precio de venta"
      description={`Precio unitario facturado al cliente en cada pedido, del más reciente al más antiguo. ${resumen}`}
    >
      {q.isLoading
        ? <Spinner />
        : rows.length === 0
          ? <p className="text-sm italic text-slate-400">Sin pedidos de cliente con precio para este producto.</p>
          : (
            <DataTable<PrecioVentaRow>
              columns={cols}
              rows={rows}
              rowKey={(r) => r.linea_id}
              defaultSort={{ key: 'fecha_pedido', desc: true }}
              syncUrl={false}
              stickyHeader={false}
              onRowClick={(r) => navigate(`${base}/pedidos/${r.pedido_cliente_id}`)}
            />
          )}
    </Card>
  );
}

export function EntregasProductoSection({ productoId, productoNombre, clienteCodigo }: { productoId: string; productoNombre: string; clienteCodigo: string }) {
  const base = `/${clienteCodigo.toLowerCase()}`;
  const qc = useQueryClient();
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['producto', productoId, 'entregas'],
    queryFn: () => api.get<{ entregas: EntregaRow[]; lineas_pendientes: LineaPendiente[] }>(`/productos/${productoId}/entregas`),
  });
  const entregas = q.data?.entregas ?? [];
  const pendientes = q.data?.lineas_pendientes ?? [];

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({
    linea_id: '',
    fecha_entrega: new Date().toISOString().slice(0, 10),
    unidades: '',
    notas: '',
  });

  const abrirAlta = () => {
    const preselect = pendientes[0]?.linea_id ?? '';
    const presetUds = pendientes[0]?.pendientes ?? '';
    setDraft({
      linea_id: preselect,
      fecha_entrega: new Date().toISOString().slice(0, 10),
      unidades: presetUds ? String(presetUds) : '',
      notas: '',
    });
    setOpen(true);
  };

  const crearEntrega = useMutation({
    mutationFn: ({ confirmar }: { confirmar?: boolean }) => {
      const linea = pendientes.find((p) => p.linea_id === draft.linea_id);
      if (!linea) throw new Error('Selecciona una línea pendiente para imputar la entrega.');
      const uds = Number(draft.unidades);
      if (!Number.isFinite(uds) || uds <= 0) throw new Error('Indica un número de unidades > 0.');
      return api.post<{
        unidades_entregadas: number;
        unidades_pendientes: number;
        sobre_entrega: boolean;
        estado_pedido: { estado_anterior: string; estado_nuevo: string } | null;
      }>(
        `/pedidos-cliente/${linea.pedido_cliente_id}/lineas/${linea.linea_id}/entregar`,
        {
          unidades: uds,
          fecha_entrega: draft.fecha_entrega || undefined,
          notas: draft.notas || undefined,
          confirmar_sobreentrega: confirmar === true,
        },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['producto', productoId, 'entregas'] });
      qc.invalidateQueries({ queryKey: ['pedidos-cliente'] });
      qc.invalidateQueries({ queryKey: ['pedidos-cliente-lineas'] });
      setOpen(false);
    },
    onError: async (err) => {
      const msg = (err as Error).message;
      if (/sobre_entrega|409/i.test(msg)) {
        const m = msg.match(/→\s*\d+:\s*(.+)$/);
        const txt = m ? m[1] : msg;
        if (await confirmar({ title: '¿Confirmar la sobre-entrega?', message: txt, confirmLabel: 'Confirmar sobre-entrega', danger: true })) {
          crearEntrega.mutate({ confirmar: true });
          return;
        }
      }
      toast.error('No se pudo guardar la entrega', msg);
    },
  });

  const eliminar = useMutation({
    mutationFn: (e: EntregaRow) =>
      api.delete<{ ok: true }>(`/pedidos-cliente/${e.pedido_cliente_id}/lineas/${e.linea_id}/entregas/${e.id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['producto', productoId, 'entregas'] });
      qc.invalidateQueries({ queryKey: ['pedidos-cliente'] });
      qc.invalidateQueries({ queryKey: ['pedidos-cliente-lineas'] });
    },
    onError: (err) => toast.error('No se pudo eliminar la entrega', (err as Error).message),
  });

  const totalPendientes = pendientes.reduce((acc, l) => acc + l.pendientes, 0);

  return (
    <Card
      title="Entregas"
      description={`Histórico de entregas del producto "${productoNombre}". ${pendientes.length > 0 ? `${totalPendientes} ud. pendientes en ${pendientes.length} línea(s).` : 'Sin líneas pendientes.'}`}
      actions={
        <Button
          variant="create"
          onClick={abrirAlta}
          disabled={pendientes.length === 0}
          title={pendientes.length === 0 ? 'No hay líneas pendientes a las que imputar la entrega.' : 'Registrar una entrega parcial o total.'}
        >
          + Añadir entrega
        </Button>
      }
    >
      {entregas.length === 0 ? (
        <p className="text-xs italic text-slate-400">Aún no hay entregas registradas para este producto.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                <th className="px-2 py-1.5">Fecha</th>
                <th className="px-2 py-1.5">Pedido</th>
                <th className="px-2 py-1.5">Estado pedido</th>
                <th className="px-2 py-1.5 text-right">Uds. entregadas</th>
                <th className="px-2 py-1.5 text-right">Línea (uds / entregadas / pdtes)</th>
                <th className="px-2 py-1.5">Notas</th>
                <th className="px-2 py-1.5">Usuario</th>
                <th className="px-2 py-1.5 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {entregas.map((e) => (
                <tr key={e.id} className="hover:bg-slate-50">
                  <td className="px-2 py-1.5 font-mono text-xs">{fmt.date(e.fecha_entrega)}</td>
                  <td className="px-2 py-1.5">
                    <button
                      type="button"
                      onClick={() => navigate(`${base}/pedidos/${e.pedido_cliente_id}?focus=${e.linea_id}`)}
                      className="cursor-pointer font-mono text-xs text-accent-700 hover:underline"
                    >
                      {e.numero_cliente ?? e.numero_approx ?? e.pedido_cliente_id.slice(0, 8)}
                    </button>
                    <div className="text-[11px] text-slate-400">{fmt.date(e.fecha_pedido)}</div>
                  </td>
                  <td className="px-2 py-1.5 text-xs">
                    <Badge color={e.pedido_estado_color} dot>{e.pedido_estado_nombre ?? e.pedido_estado}</Badge>
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono text-sm font-semibold">{fmt.int(e.unidades)}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-xs text-slate-600">
                    {fmt.int(e.linea_unidades)} / {fmt.int(e.linea_entregadas)} /{' '}
                    <span className={e.linea_pendientes > 0 ? 'font-semibold text-bad-700' : 'text-ok-700'}>
                      {fmt.int(e.linea_pendientes)}
                    </span>
                  </td>
                  <td className="px-2 py-1.5 text-xs text-slate-600">{e.notas ?? <span className="text-slate-300">—</span>}</td>
                  <td className="px-2 py-1.5 text-xs text-slate-500">{e.created_by_email ?? '—'}</td>
                  <td className="px-2 py-1.5 text-right">
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={async () => {
                        if (await confirmar({ title: '¿Eliminar esta entrega?', message: `Entrega de ${e.unidades} ud. del ${fmt.date(e.fecha_entrega)}.`, confirmLabel: 'Eliminar', danger: true })) {
                          eliminar.mutate(e);
                        }
                      }}
                      disabled={eliminar.isPending}
                    >
                      🗑
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Añadir entrega"
        size="md"
        footer={
          <>
            <Button variant="danger" onClick={() => setOpen(false)} disabled={crearEntrega.isPending}>Cancelar</Button>
            <Button
              variant="save"
              onClick={() => crearEntrega.mutate({ confirmar: false })}
              disabled={crearEntrega.isPending || !draft.linea_id || !draft.unidades || Number(draft.unidades) <= 0}
            >
              {crearEntrega.isPending ? 'Guardando…' : '✓ Registrar entrega'}
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-sm">
          {pendientes.length === 0 ? (
            <p className="text-xs italic text-slate-500">No hay líneas pendientes de entrega para este producto.</p>
          ) : (
            <>
              <FormRow label="Línea / Pedido al que imputar">
                <Select
                  options={pendientes.map((l) => ({
                    value: l.linea_id,
                    label: `Pedido ${l.numero_cliente ?? l.numero_approx ?? l.pedido_cliente_id.slice(0, 8)} · ${l.unidades} previstas · ${l.pendientes} pdte.`,
                  }))}
                  value={draft.linea_id}
                  emptyLabel="— elegir —"
                  onChange={(e) => {
                    const id = e.target.value;
                    const linea = pendientes.find((p) => p.linea_id === id);
                    setDraft((d) => ({
                      ...d,
                      linea_id: id,
                      unidades: linea?.pendientes ? String(linea.pendientes) : d.unidades,
                    }));
                  }}
                />
              </FormRow>
              <FormRow label="Unidades entregadas en esta operación">
                <Input
                  type="number"
                  min={1}
                  value={draft.unidades}
                  onChange={(e) => setDraft((d) => ({ ...d, unidades: e.target.value }))}
                />
              </FormRow>
              <FormRow label="Fecha de la entrega" hint="Por defecto hoy.">
                <Input
                  type="date"
                  value={draft.fecha_entrega}
                  onChange={(e) => setDraft((d) => ({ ...d, fecha_entrega: e.target.value }))}
                />
              </FormRow>
              <FormRow label="Notas (opcional)">
                <Input
                  value={draft.notas}
                  onChange={(e) => setDraft((d) => ({ ...d, notas: e.target.value }))}
                  placeholder="Ej: 2ª remesa, mensajero MRW guía 12345"
                />
              </FormRow>
            </>
          )}
        </div>
      </Modal>
    </Card>
  );
}
